import type { AppRole } from '../permissions/app-permissions';

// Measurement system preference
export type MeasurementSystem = 'metric' | 'imperial';

// Supported language codes (ISO 639-1)
export type LanguageCode = 'en' | 'fr' | 'es' | 'de' | 'it' | 'nl' | 'pt' | 'ja' | 'zh';

export const SUPPORTED_LANGUAGES: { code: LanguageCode; name: string }[] = [
  { code: 'en', name: 'English' },
  { code: 'fr', name: 'Français' },
  { code: 'es', name: 'Español' },
  { code: 'de', name: 'Deutsch' },
  { code: 'it', name: 'Italiano' },
  { code: 'nl', name: 'Nederlands' },
  { code: 'pt', name: 'Português' },
  { code: 'ja', name: '日本語' },
  { code: 'zh', name: '中文' },
];

/** Array of supported language codes for validation */
export const LANGUAGE_CODES: LanguageCode[] = SUPPORTED_LANGUAGES.map((l) => l.code);

/**
 * Default language for each supported country.
 * Used when neither user nor household has a language preference set.
 */
export const COUNTRY_DEFAULT_LANGUAGES: Record<string, LanguageCode> = {
  us: 'en',
  uk: 'en',
  ca: 'en',
  au: 'en',
  nz: 'en',
  ie: 'en',
  fr: 'fr',
  be: 'fr',
  ch: 'fr',
  de: 'de',
  at: 'de',
  es: 'es',
  mx: 'es',
  it: 'it',
  nl: 'nl',
  br: 'pt',
  jp: 'ja',
  sg: 'en',
  in: 'en',
  world: 'en',
};

// User types - synced with Supabase Auth
export interface User {
  id: string;
  email: string;
  name: string | null;
  avatarUrl: string | null;
  measurementSystem: MeasurementSystem | null; // null = not set, use device locale
  language: LanguageCode | null; // null = use household default
  appRole: AppRole; // App-level admin role
  lastLoginAt: Date | null; // Track last login for admin dashboard
  // Account type flags for subscription handling
  isBetaUser: boolean; // Beta period active - new users get grandfathering
  isDemoAccount: boolean; // Auto-receives premium features and all future tiers
  betaGrandfatheringEndsAt: Date | null; // Set to launch_date + 60 days when subscriptions go live
  createdAt: Date;
  updatedAt: Date;
}

// ============================================================================
// SUBSCRIPTION TYPES
// ============================================================================

/**
 * Subscription tiers available for households.
 * - free: Default tier, single member
 * - premium: Up to 5 members, unlimited history
 * - business: Up to 10 members, advanced analytics
 */
export type SubscriptionTier = 'free' | 'premium' | 'business';

/**
 * Current status of a subscription.
 * - active: Subscription is current and paid
 * - past_due: Payment failed but still active (grace period)
 * - canceled: Subscription has been canceled
 * - trialing: In free trial period
 */
export type SubscriptionStatus = 'active' | 'past_due' | 'canceled' | 'trialing';

/**
 * Source of the subscription payment.
 * - paddle: Web subscription via Paddle (merchant of record)
 * - adapty: Mobile subscription via Adapty (iOS/Android stores)
 * - null: Free tier, no payment source
 */
export type SubscriptionSource = 'paddle' | 'adapty' | null;

/**
 * Tier-specific limits for households.
 * -1 means unlimited.
 */
export const TIER_LIMITS = {
  free: { maxHouseholds: 1, maxMembers: 1, historyDays: 30 },
  premium: { maxHouseholds: 3, maxMembers: 5, historyDays: -1 },
  business: { maxHouseholds: -1, maxMembers: 10, historyDays: -1 },
} as const;

/**
 * Pricing in cents for web subscriptions via Paddle.
 * Mobile prices are set in Adapty dashboard (typically 20-30% higher).
 */
export const PRICING = {
  premium: { monthly: 399, yearly: 2999 },
  business: { monthly: 999, yearly: 7999 },
} as const;

// ============================================================================
// HOUSEHOLD TYPES
// ============================================================================

// Household types
export interface Household {
  id: string;
  name: string;
  country: string;
  language: LanguageCode | null; // null = use country default language
  // Subscription fields
  subscriptionTier: SubscriptionTier;
  subscriptionStatus: SubscriptionStatus;
  subscriptionSource: SubscriptionSource;
  trialEndsAt: Date | null;
  currentPeriodEndsAt: Date | null;
  maxMembers: number;
  expiryThresholdDays: number;
  dashboardExpiryDays: number;
  createdAt: Date;
  updatedAt: Date;
}

/**
 * Supported country codes for Open Food Facts regional search.
 * Uses OFF subdomain format. 'world' searches all countries.
 */
export const OFF_COUNTRIES = [
  { code: 'world', name: 'Worldwide' },
  { code: 'us', name: 'United States' },
  { code: 'uk', name: 'United Kingdom' },
  { code: 'ca', name: 'Canada' },
  { code: 'au', name: 'Australia' },
  { code: 'fr', name: 'France' },
  { code: 'de', name: 'Germany' },
  { code: 'es', name: 'Spain' },
  { code: 'it', name: 'Italy' },
  { code: 'nl', name: 'Netherlands' },
  { code: 'be', name: 'Belgium' },
  { code: 'ch', name: 'Switzerland' },
  { code: 'at', name: 'Austria' },
  { code: 'pt', name: 'Portugal' },
  { code: 'ie', name: 'Ireland' },
  { code: 'nz', name: 'New Zealand' },
  { code: 'mx', name: 'Mexico' },
  { code: 'br', name: 'Brazil' },
  { code: 'jp', name: 'Japan' },
  { code: 'kr', name: 'South Korea' },
  { code: 'cn', name: 'China' },
  { code: 'in', name: 'India' },
  { code: 'sg', name: 'Singapore' },
  { code: 'se', name: 'Sweden' },
  { code: 'no', name: 'Norway' },
  { code: 'dk', name: 'Denmark' },
  { code: 'fi', name: 'Finland' },
  { code: 'pl', name: 'Poland' },
] as const;

export type OFFCountryCode = (typeof OFF_COUNTRIES)[number]['code'];

export const VALID_OFF_COUNTRY_CODES = OFF_COUNTRIES.map((c) => c.code);

// Household member roles - single source of truth is in permissions/household-permissions.ts
import { type HouseholdRole } from '../permissions/household-permissions';

export interface HouseholdMember {
  id: string;
  householdId: string;
  userId: string;
  role: HouseholdRole;
  joinedAt: Date;
}

// Invitation role (owner is only assigned when creating household)
export type InvitationRole = 'admin' | 'member';

// Invitation status (derived from acceptedAt and expiresAt in DB)
export type InvitationStatus = 'pending' | 'accepted' | 'expired' | 'revoked';

export interface HouseholdInvitation {
  id: string;
  householdId: string;
  email: string;
  role: InvitationRole;
  invitedBy: string | null;
  token: string;
  expiresAt: Date;
  acceptedAt: Date | null;
  createdAt: Date;
}

// Item unit types (moved before Product for dependency order)
export type ItemUnit =
  | 'item'
  | 'kg'
  | 'lb'
  | 'oz'
  | 'g'
  | 'l'
  | 'ml'
  | 'floz'
  | 'cup'
  | 'pt'
  | 'qt'
  | 'gal';

export const ITEM_UNITS: ItemUnit[] = [
  'item',
  'kg',
  'lb',
  'oz',
  'g',
  'l',
  'ml',
  'floz',
  'cup',
  'pt',
  'qt',
  'gal',
];

/**
 * Human-readable display labels for units with proper capitalization.
 * Use these for UI display instead of the raw unit values.
 */
export const UNIT_DISPLAY_LABELS: Record<ItemUnit, string> = {
  item: 'item',
  g: 'g',
  kg: 'kg',
  oz: 'oz',
  lb: 'lb',
  ml: 'mL',
  l: 'L',
  floz: 'fl oz',
  cup: 'cup',
  pt: 'pt',
  qt: 'qt',
  gal: 'gal',
};

// Product types
export type ProductSource = 'openfoodfacts' | 'user' | 'manual';

export interface NutritionData {
  energy_kcal?: number;
  fat?: number;
  saturated_fat?: number;
  carbohydrates?: number;
  sugars?: number;
  fiber?: number;
  proteins?: number;
  salt?: number;
  sodium?: number;
  [key: string]: number | undefined;
}

/**
 * Multilingual product names structure.
 * OFF names are stored at the root level by language code.
 * User-contributed translations are stored under the `_user` key.
 *
 * Example:
 * {
 *   "en": "Chocolate",
 *   "fr": "Chocolat",
 *   "_user": {
 *     "de": "Schokolade"  // User-contributed German name
 *   }
 * }
 *
 * TODO: When OFF Write API integration is complete, user contributions
 * should be submitted back to OFF. See docs/TODO-OFF-INTEGRATION.md
 */
export interface ProductNames {
  [languageCode: string]: string | { [lang: string]: string } | undefined;
  _user?: { [languageCode: string]: string };
}

/**
 * Multilingual text content (ingredients, etc.)
 */
export interface MultilingualText {
  [languageCode: string]: string | undefined;
}

/**
 * Product images from Open Food Facts, keyed by type and language.
 * Example: { "front_en": "url", "ingredients_fr": "url", "nutrition_en": "url" }
 */
export interface ProductImages {
  [key: string]: string | undefined;
}

/**
 * Eco-Score grade for environmental impact.
 * 'a' is best, 'e' is worst.
 */
export type EcoScoreGrade = 'a' | 'b' | 'c' | 'd' | 'e';

/**
 * Nutri-Score grade for nutritional quality.
 * 'a' is best, 'e' is worst.
 */
export type NutriScoreGrade = 'a' | 'b' | 'c' | 'd' | 'e';

/**
 * NOVA food processing classification.
 * 1: Unprocessed or minimally processed
 * 2: Processed culinary ingredients
 * 3: Processed foods
 * 4: Ultra-processed foods
 */
export type NovaGroup = 1 | 2 | 3 | 4;

export interface Product {
  id: string;
  barcode: string | null;
  name: string; // Legacy field - primary name for display
  brand: string | null;
  description: string | null;
  imageUrl: string | null;
  category: string | null;
  nutritionData: NutritionData | null;
  source: ProductSource;
  sourceId: string | null;
  defaultShelfLifeDays: number | null;
  defaultQuantity: number | null;
  defaultUnit: ItemUnit | null;
  rawUnit: string | null;
  // Enhanced multilingual and OFF data
  names: ProductNames | null; // Multilingual product names
  ingredientsText: MultilingualText | null; // Multilingual ingredients
  allergensTags: string[] | null; // e.g., ['en:gluten', 'en:milk']
  labelsTags: string[] | null; // e.g., ['en:organic', 'en:fair-trade']
  ecoscoreGrade: EcoScoreGrade | null;
  nutriscoreGrade: NutriScoreGrade | null;
  novaGroup: NovaGroup | null;
  images: ProductImages | null;
  offLastModified: string | null; // For detecting upstream changes
  offCategoryTags: string[] | null; // OFF taxonomy tags for product group matching
  createdAt: Date;
  updatedAt: Date;
}

/**
 * Resolves the best product name for a given language preference.
 * Priority: user contribution > OFF name in preferred lang > English > any available > legacy name
 *
 * @param product - The product to get the name for
 * @param preferredLang - ISO 639-1 language code (e.g., 'en', 'fr')
 * @returns The best available product name, guaranteed to be a non-empty string
 */
export function resolveProductName(product: Product, preferredLang: string): string {
  const names = product.names;

  if (!names) {
    return product.name;
  }

  // 1. Check user contributions first
  const userContribution = names._user?.[preferredLang];
  if (userContribution && typeof userContribution === 'string' && userContribution.trim()) {
    return userContribution;
  }

  // 2. Check OFF name in preferred language
  const offName = names[preferredLang];
  if (typeof offName === 'string' && offName.trim()) {
    return offName;
  }

  // 3. Fallback to English
  const englishName = names['en'];
  if (typeof englishName === 'string' && englishName.trim()) {
    return englishName;
  }

  // 4. Fallback to any available OFF name
  for (const key of Object.keys(names)) {
    if (key !== '_user' && key !== '_default') {
      const value = names[key];
      if (typeof value === 'string' && value.trim()) {
        return value;
      }
    }
  }

  // 5. Check _default key (used when language is unknown)
  const defaultName = names['_default'];
  if (typeof defaultName === 'string' && defaultName.trim()) {
    return defaultName;
  }

  // 6. Final fallback to legacy name field
  return product.name;
}

/**
 * Checks if a product has a translation available in the preferred language.
 * Returns information about the translation status for UI display.
 *
 * @param product - Product with potential multilingual names
 * @param preferredLang - ISO 639-1 language code
 * @returns Translation status information
 */
export function getProductTranslationStatus(
  product: Product,
  preferredLang: string
): {
  hasPreferredTranslation: boolean;
  hasUserContribution: boolean;
  fallbackLanguage: string | null;
  canContribute: boolean;
} {
  const names = product.names;

  // If no names object, no multilingual support
  if (!names) {
    return {
      hasPreferredTranslation: false,
      hasUserContribution: false,
      fallbackLanguage: null,
      canContribute: false, // Can't contribute to products without names structure
    };
  }

  // Check for user contribution in preferred language
  const hasUserContribution = !!(
    names._user?.[preferredLang] &&
    typeof names._user[preferredLang] === 'string' &&
    names._user[preferredLang].trim()
  );

  // Check for OFF translation in preferred language
  const hasOffTranslation = !!(
    names[preferredLang] &&
    typeof names[preferredLang] === 'string' &&
    (names[preferredLang] as string).trim()
  );

  const hasPreferredTranslation = hasUserContribution || hasOffTranslation;

  // Determine fallback language if no preferred translation
  let fallbackLanguage: string | null = null;
  if (!hasPreferredTranslation) {
    if (names['en'] && typeof names['en'] === 'string' && names['en'].trim()) {
      fallbackLanguage = 'en';
    } else {
      for (const key of Object.keys(names)) {
        if (key !== '_user' && key !== '_default' && key !== preferredLang) {
          const value = names[key];
          if (typeof value === 'string' && value.trim()) {
            fallbackLanguage = key;
            break;
          }
        }
      }
    }
  }

  return {
    hasPreferredTranslation,
    hasUserContribution,
    fallbackLanguage,
    canContribute: true, // Products with names structure can receive contributions
  };
}

/**
 * Resolves the display language for a user within a household.
 * Priority: user language > household language > country default > 'en'
 */
export function resolveDisplayLanguage(
  user: Pick<User, 'language'>,
  household: Pick<Household, 'language' | 'country'>
): LanguageCode {
  if (user.language) return user.language;
  if (household.language) return household.language;
  return COUNTRY_DEFAULT_LANGUAGES[household.country] ?? 'en';
}

export const PRODUCT_CATEGORIES = [
  'dairy',
  'produce',
  'meat',
  'seafood',
  'frozen',
  'bakery',
  'beverages',
  'snacks',
  'condiments',
  'canned',
  'dry-goods',
  'household',
  'personal-care',
  'other',
] as const;

export type ProductCategory = (typeof PRODUCT_CATEGORIES)[number];

// Storage location types
export type StorageLocationType = 'fridge' | 'freezer' | 'pantry' | 'other';

export const STORAGE_LOCATION_TYPES: StorageLocationType[] = [
  'fridge',
  'freezer',
  'pantry',
  'other',
];

export interface StorageLocation {
  id: string;
  householdId: string;
  name: string;
  type: StorageLocationType;
  icon: string | null;
  sortOrder: number;
  expiryThresholdDays: number | null;
  createdAt: Date;
  updatedAt: Date;
  /** Number of items in this location (included in list responses) */
  itemCount?: number;
}

// Item instance status types
export type ItemInstanceStatus = 'active' | 'consumed' | 'wasted' | 'expired';

export const ITEM_INSTANCE_STATUSES: ItemInstanceStatus[] = [
  'active',
  'consumed',
  'wasted',
  'expired',
];

// Waste reason types - for tracking why items were wasted
export type WasteReason = 'expired' | 'spoiled' | 'damaged' | 'disliked' | 'overstock' | 'other';

export const WASTE_REASONS: WasteReason[] = [
  'expired',
  'spoiled',
  'damaged',
  'disliked',
  'overstock',
  'other',
];

export const WASTE_REASON_LABELS: Record<WasteReason, string> = {
  expired: 'Expired',
  spoiled: 'Spoiled before expiry',
  damaged: 'Damaged or spilled',
  disliked: "Didn't like taste/quality",
  overstock: 'Bought too much',
  other: 'Other reason',
};

// Consume reason types - for tracking how items were used
export type ConsumeReason = 'cooked' | 'eaten_raw' | 'shared' | 'donated' | 'other';

export const CONSUME_REASONS: ConsumeReason[] = [
  'cooked',
  'eaten_raw',
  'shared',
  'donated',
  'other',
];

// Created from - tracks which platform created an item
export type CreatedFrom = 'web' | 'mobile' | 'api';

export const CREATED_FROM_VALUES: CreatedFrom[] = ['web', 'mobile', 'api'];

export const CONSUME_REASON_LABELS: Record<ConsumeReason, string> = {
  cooked: 'Cooked/Prepared',
  eaten_raw: 'Eaten Fresh',
  shared: 'Shared with Others',
  donated: 'Donated/Given away',
  other: 'Other reason',
};

// Item types
export interface Item {
  id: string;
  householdId: string;
  productId: string | null;
  storageLocationId: string | null;
  customName: string | null;
  quantity: number;
  unit: ItemUnit;
  notes: string | null;
  lowStockThreshold: number | null;
  targetQuantity: number | null;
  targetUnit: string | null;
  expiryThresholdDays: number | null;
  createdFrom: CreatedFrom | null;
  createdAt: Date;
  updatedAt: Date;
}

/**
 * Item with joined product and storage location data.
 * Returned by the items list API endpoint.
 */
export interface ItemWithRelations extends Item {
  product: Product | null;
  storageLocation: StorageLocation | null;
}

// Item instance types - tracks individual item lifecycle
export interface ItemInstance {
  id: string;
  itemId: string;
  quantity: number;
  purchaseDate: string | null;
  purchasePrice: number | null;
  expiryDate: string | null;
  notes: string | null;
  status: ItemInstanceStatus;
  statusChangedAt: Date | null;
  statusChangedBy: string | null;
  statusNotes: string | null;
  wasteReason: WasteReason | null;
  consumeReason: ConsumeReason | null;
  createdAt: Date;
  updatedAt: Date;
}

/**
 * Item with computed instance counts.
 * Returned by the items list API when instance counts are needed.
 */
export interface ItemWithInstanceCounts extends ItemWithRelations {
  activeQuantity: number; // Sum of active instance quantities
  activeInstanceCount: number; // Count of active instances
  earliestExpiryDate?: string | null; // Earliest expiry date among active instances
}

// Dashboard statistics
export interface DashboardStats {
  totalItems: number;
  expiringSoon: number;
  expired: number;
  /** Inventory health score (0-100) using time-based decay weighted by quantity */
  healthScore: number;
}

// Preset types (quantity & expiry)
export * from './presets';

// Batch entry types
export * from './batch-entry';

// Notification types
export * from './notification';

// Product search types
export * from './products';

// Announcement types
export * from './announcement';

// Item form types
export * from './item-form';

// View preferences types
export type ViewMode = 'grouped' | 'flat';
export type InventorySortBy = 'expiry_date' | 'name' | 'created_at';
export type SortOrder = 'asc' | 'desc';
export type ViewPlatform = 'web' | 'mobile';

export interface ViewPreferences {
  id: string;
  userId: string;
  platform: ViewPlatform;
  viewMode: ViewMode;
  sortBy: InventorySortBy;
  sortOrder: SortOrder;
  createdAt: Date;
  updatedAt: Date;
}

export const DEFAULT_VIEW_PREFERENCES: Pick<ViewPreferences, 'viewMode' | 'sortBy' | 'sortOrder'> =
  {
    viewMode: 'grouped',
    sortBy: 'created_at',
    sortOrder: 'desc',
  };

// Product group types
export * from './product-groups';

// Shopping trip types
export * from './shopping-trips';

// Consolidated item types
export * from './consolidated-item';
